/**
 * Nederlandse opmaak van decimale getallen die als tekst uit de API komen.
 *
 * De API levert bedragen als Decimal-tekst ("105051.70"), en een scherm dat
 * die kaal toont zet `€ 105051.70` naast een kolom die `€ 112.051,70` zegt —
 * zo stond het op 23-09-2026 op het scherm Projecten. Deze functies volgen
 * dezelfde regel als `presentation.eur_exact` in de backend: punten tussen de
 * duizendtallen, een komma voor de centen, een min-teken als "−".
 *
 * **Teken voor een bedrag (ronde 9, één regel):** het teken, een harde spatie,
 * dan het euroteken: "− € 2.482" en "+ € 17,1 mln", zoals de mockups ("+ € 9,8
 * mln" op 25, "− € 610" op 22) en `presentation.eur` in de backend. Een getal
 * zonder euroteken houdt zijn minteken vast aan het getal ("−62", "−2,28 %").
 *
 * Er wordt niet gerekend met floats: `Number("0.1") + …` of `toLocaleString`
 * op een groot bedrag kan een cent verschuiven, en dan staat er op het scherm
 * een ander getal dan in de run. Alles gaat op de cijferreeks zelf.
 */

const DECIMAAL = /^(-?)(\d+)(?:\.(\d+))?$/;

const groepeer = (cijfers: string) => cijfers.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

/** Het teken vóór een bedrag, met een harde spatie naar het euroteken (ronde 9). */
const MIN_BEDRAG = "−\u00a0";
const PLUS_BEDRAG = "+\u00a0";

/** Rondt een positieve decimale tekst half-up af op `decimalen` cijfers. */
function rondAf(heel: string, deel: string, decimalen: number): [string, string] {
  if (deel.length <= decimalen) return [heel, deel.padEnd(decimalen, "0")];
  const kort = BigInt(heel + deel.slice(0, decimalen)) + (Number(deel[decimalen]) >= 5 ? 1n : 0n);
  const tekst = kort.toString().padStart(decimalen + 1, "0");
  return decimalen === 0
    ? [tekst, ""]
    : [tekst.slice(0, -decimalen), tekst.slice(-decimalen)];
}

/**
 * Een bedrag met centen: "105051.70" → "€ 105.051,70", "-98374" → "− € 98.374,00". Leeg → "n.v.t.".
 * Het minteken staat vóór het euroteken, met een harde spatie, zoals `presentation.eur_exact`.
 */
export function bedrag(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "n.v.t.";
  const match = DECIMAAL.exec(String(value).trim());
  if (!match) return `€\u00a0${value}`;
  const [, min, heel, deel = ""] = match;
  const [euro, centen] = rondAf(heel, deel, 2);
  const nul = /^0+$/.test(euro + centen);
  return `${min && !nul ? MIN_BEDRAG : ""}€\u00a0${groepeer(euro.replace(/^0+(?=\d)/, ""))},${centen}`;
}

/**
 * Een getal zonder eenheid of met een eenheid erachter (m², %, factor):
 * "56.25" → "56,25", "3.000000" → "3", "0.300000" → "0,3". Nullen achter de
 * komma die de database meegeeft ("83.000000 %") vallen weg; een getal dat
 * geen decimale tekst is, komt ongewijzigd terug.
 */
export function getal(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "n.v.t.";
  const match = DECIMAAL.exec(String(value).trim());
  if (!match) return String(value);
  const [, min, heel, deel = ""] = match;
  const rest = deel.replace(/0+$/, "");
  const nul = /^0+$/.test(heel) && rest === "";
  return `${min && !nul ? "−" : ""}${groepeer(heel.replace(/^0+(?=\d)/, ""))}${rest ? `,${rest}` : ""}`;
}

/**
 * Een percentage met een vast aantal decimalen: "3.0179" → "3,02 %", "2.9300" → "2,93 %".
 * Voor IRR's die naast elkaar staan: `getal` laat de nullen vallen, en dan staat
 * "3,0179 %" naast "2,93 %" (verbeterscherm, 21-10-2026).
 */
export function percentage(value: string | null | undefined, decimalen = 2): string {
  if (value === null || value === undefined || value === "") return "n.v.t.";
  const match = DECIMAAL.exec(String(value).trim());
  if (!match) return `${value}\u00a0%`;
  const [, min, heel, deel = ""] = match;
  const [geheel, rest] = rondAf(heel, deel, decimalen);
  const nul = /^0+$/.test(geheel + rest);
  return `${min && !nul ? "−" : ""}${groepeer(geheel.replace(/^0+(?=\d)/, ""))}${rest ? `,${rest}` : ""}\u00a0%`;
}

/**
 * Een bedrag in hele euro's: "1916174.08" → "€ 1.916.174". Voor een vergelijking van
 * zevencijferige waarden naast elkaar, waar de cent alleen breedte kost.
 */
export function bedragRond(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "n.v.t.";
  const match = DECIMAAL.exec(String(value).trim());
  if (!match) return `€\u00a0${value}`;
  const [, min, heel, deel = ""] = match;
  const [euro] = rondAf(heel, deel, 0);
  const nul = /^0+$/.test(euro);
  return `${min && !nul ? MIN_BEDRAG : ""}€\u00a0${groepeer(euro.replace(/^0+(?=\d)/, ""))}`;
}

/**
 * Een getal half-up afgerond op `decimalen`, met Nederlandse opmaak: "3867.3654" → "3.867,4".
 * Leeg geeft "n.v.t." (plan 22: een kenmerk zonder waarde heet zo, niet "0").
 */
export function afgerond(value: string | number | null | undefined, decimalen = 1): string {
  if (value === null || value === undefined || value === "") return "n.v.t.";
  const match = DECIMAAL.exec(String(value).trim());
  if (!match) return String(value);
  const [, min, heel, deel = ""] = match;
  const [geheel, rest] = rondAf(heel, deel, decimalen);
  const nul = /^0+$/.test(geheel + rest);
  return `${min && !nul ? "−" : ""}${groepeer(geheel.replace(/^0+(?=\d)/, ""))}${rest ? `,${rest}` : ""}`;
}

/* ------------------------------------------------------------ bedragen */

/**
 * De regel voor bedragen (ronde 5, één keer voor alle schermen):
 *
 * - **Per eenheid, per maand, per jaar per woning, grenzen en tarieven:** het
 *   volle bedrag ("€ 124.781", "€ 597", "€ 498,20").
 * - **Totalen over meer eenheden** (portefeuille, selectie, complex, programma)
 *   vanaf € 1 mln: "€ x,x mln" met één decimaal, zoals de API ("€ 421,9 mln").
 * - **Binnen één kolom of één kaart altijd dezelfde notatie.** Is één bedrag in
 *   de kolom of kaart € 1 mln of meer, dan staan ze allemaal in miljoenen,
 *   ook "€ 0,1 mln" (mockup 07). `bedragNotatie` kiest, `bedragMln` schrijft,
 *   `ValueCell amount="mln"` doet het in een tabel.
 *
 * Dit is opmaak van het getal dat de API levert (`plot`), geen berekening.
 */
export const MLN_VANAF = 1_000_000;

/** Welke notatie een kolom of kaart met deze bedragen krijgt. */
export function bedragNotatie(waarden: Array<number | null | undefined>): "mln" | "vol" {
  return waarden.some((waarde) => typeof waarde === "number" && Math.abs(waarde) >= MLN_VANAF) ? "mln" : "vol";
}

/** Een bedrag in miljoenen met één decimaal: 4016463 → "€ 4,0 mln", 121911 → "€ 0,1 mln". */
export function bedragMln(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "n.v.t.";
  // Afronden op honderdduizend euro, half naar boven, zoals `presentation.eur`.
  const tienden = Math.floor(Math.abs(value) / 100_000 + 0.5);
  const nul = tienden === 0;
  return `${value < 0 && !nul ? MIN_BEDRAG : ""}€\u00a0${groepeer(String(Math.floor(tienden / 10)))},${tienden % 10} mln`;
}

/**
 * Een verschil in hele euro's met een teken: "1250.4" → "+ € 1.250", "-610" →
 * "− € 610", nul → "€ 0" (ronde 9). Voor een afwijking of een huurruimte.
 */
export function bedragVerschil(value: string | number | null | undefined): string {
  const tekst = bedragRond(value);
  return /^€\u00a0/.test(tekst) && !/^€\u00a00$/.test(tekst) ? `${PLUS_BEDRAG}${tekst}` : tekst;
}

/**
 * Een bedrag dat de API al heeft opgemaakt (`display`), met een plusteken als
 * het getal (`plot`) positief is: "+ € 17,1 mln". Een negatief bedrag heeft zijn
 * teken al; een oude schrijfwijze "−€" krijgt de harde spatie (ronde 9).
 */
export function metTeken(display: string, plot: number | null | undefined): string {
  const tekst = display.replace(/^[−-]\s?€/, `${MIN_BEDRAG}€`);
  return typeof plot === "number" && plot > 0 && !/^[+−-]/.test(tekst) ? `${PLUS_BEDRAG}${tekst}` : tekst;
}

/* ------------------------------------------------------- tekst zetten */

/**
 * Een aandeel dat het scherm al als getal heeft (uit een telling van de API):
 * 92.66 → "92,7 %", 100 → "100 %". Met een harde spatie, zoals de API.
 */
export function procent(value: number, maxDecimalen = 1): string {
  return `${value.toLocaleString("nl-NL", { maximumFractionDigits: maxDecimalen })}\u00a0%`;
}

/**
 * Een procent dat als decimale tekst uit de API komt, zonder af te ronden:
 * "5.5" → "5,5 %", "21.000000" → "21 %", "-2.25" → "−2,25 %". Met een harde
 * spatie (ronde 9). Leeg → "n.v.t.". Voor vaste decimalen: `percentage`.
 */
export function procentTekst(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "n.v.t.";
  return `${getal(value)}\u00a0%`;
}

/**
 * Een getal en zijn eenheid breken niet van elkaar af: "40 %", "€ 10.017",
 * "€ 3,1 mln" en "2 pp" krijgen een harde spatie (controle ronde 4: "40 / %",
 * "€ / 10.017"), net als "≤ 30" en een datum "30-09-2026" (ronde 11), "minimaal
 * 40 %", een norm als "NEN 2580" en een naam als "Q-Estate" (ronde 13), " · ",
 * "FB-054", "1 januari" en "1 = uitstekend" (ronde 15), en "1950 t/m 1970", "en
 * 6,73 %", "CC BY 4.0" en "overschot-PMC" (ronde 17). Voor tekst die niet via de
 * schrijfwijze van de API komt.
 */
export const keepUnits = (text: string) =>
  text
    .replace(/ %/g, "\u00a0%")
    .replace(/(\d) (mln|pp)(?=$|[\s.,;:)])/g, "$1\u00a0$2")
    .replace(/€ (?=[\d−-])/g, "€\u00a0")
    // Het teken voor een bedrag: "−€ 5" en "+ € 5" worden "− € 5" en "+ € 5" (ronde 9).
    .replace(/(^|[\s(])([−+]) ?€/g, "$1$2\u00a0€")
    // Ronde 11, dezelfde regels als `display_text` in de API: een vergelijkingsteken
    // blijft bij zijn getal ("≤ 30"), en een datum breekt niet op zijn streepjes
    // ("30-09-2026" met een onzichtbare woordverbinder na elk streepje).
    .replace(/([≤≥<>]) (?=[\d−€])/g, "$1\u00a0")
    .replace(/(?<![\w./-])(\d{1,2})-(\d{1,2})-((?:19|20)\d{2})(?![\w-])/g, "$1-\u2060$2-\u2060$3")
    // Ronde 13, ook zoals `display_text`: "minimaal 40 %" en "NEN 2580" blijven heel,
    // en een naam als "Q-Estate" breekt niet op zijn koppelteken.
    .replace(/\b(minimaal|maximaal|minstens|hoogstens|ten minste|ten hoogste) (?=[\d−€])/g, "$1\u00a0")
    .replace(/(?<![\w./-])((?:NEN|NTA|NPR|ISSO|ISO|BRL|EN)(?:-(?:EN|ISO))*) (?=\d)/g, (_, norm: string) =>
      `${norm.replace(/-/g, "-\u2060")}\u00a0`,
    )
    .replace(/(?<![\w./@-])([A-Z]{1,3})-(?=[A-Z][a-z])/g, "$1-\u2060")
    // Ronde 15, ook zoals `display_text` (controleronde 14): het scheidingsteken " · "
    // hangt met een harde spatie aan het woord ervoor, zodat een regel nooit met de
    // punt begint; een code met een nummer ("FB-054", "KD-10") breekt niet op zijn
    // koppelteken; een dag blijft bij zijn maand ("1 januari"); "1 = uitstekend" blijft heel.
    .replace(/ · /g, "\u00a0· ")
    .replace(/(?<![\w./@-])([A-Z]{1,4})-(?=\d)/g, "$1-\u2060")
    .replace(
      /(?<![\w.,-])(\d{1,2}) (januari|februari|maart|april|mei|juni|juli|augustus|september|oktober|november|december)\b/g,
      "$1\u00a0$2",
    )
    .replace(/(?<=\d) = (?=\S)/g, "\u00a0=\u00a0")
    // Een losse hoofdletter bij zijn aantal: "E 98 · F 34" breekt niet als "E / 98".
    .replace(/(?<![\w./-])([A-Z]) (?=\d)/g, "$1\u00a0")
    // Een jaarreeks ("1945 tot 1969"), een aantal bij zijn telwoord ("1.749 eenheden") en
    // een voorzetsel bij zijn getal ("van 3.707", niet voor een datum) breken niet.
    // Ronde 17: ook een jaarreeks met "t/m" ("1950 t/m 1970"), "en" voor een getal in een
    // opsomming ("4,78 % en 6,73 %"), een licentie ("CC BY 4.0") en een woord met een
    // afkorting erachter ("overschot-PMC", woordverbinder na het streepje).
    .replace(/(?<![\w./-])((?:19|20)\d{2}) (tot|t\/m) ((?:19|20)\d{2})(?![\w-])/g, "$1\u00a0$2\u00a0$3")
    .replace(/(?<=\d) (eenheden|eenheid|woningen|woning|complexen|complex|panden|pand|buurten|buurt|wijken|wijk|VHE|vhe)(?![\w-])/g, "\u00a0$1")
    .replace(/\b(van|op|uit|over|per|tot|na|voor|met|in|bij|onder|boven|sinds|vanaf|en) (?=\d)(?!\d+-)/g, "$1\u00a0")
    .replace(/\bCC (BY(?:-(?:SA|NC|ND))*) (\d(?:\.\d)?)(?!\d)/g, (_, soort: string, versie: string) =>
      `CC\u00a0${soort.replace(/-/g, "-\u2060")}\u00a0${versie}`,
    )
    .replace(/(?<![\w./@-])([a-z]{2,})-(?=[A-Z]{2,5}(?![\w]))/g, "$1-\u2060");

/**
 * Het scheidingsteken " · " tussen delen van een tekst, met een gewone of (sinds
 * ronde 15, `display_text` en `keepUnits`) een harde spatie ervoor. Om op te splitsen.
 */
export const SCHEIDING = /[ \u00a0]· /;

/**
 * Ronde 17 (controleronde 16: "1008 eenheden" op 06): een tekst uit de API of uit
 * `keepUnits` heeft op veel plekken een harde spatie (U+00A0) waar eerst een gewone
 * stond ("1.008 eenheden", "van 940", "€ 713,02"), en een woordverbinder (U+2060) na
 * een koppelteken ("FB-054", "Q-Estate"). Een RegExp die zo'n tekst leest, rekent daarom
 * nooit op een gewone spatie: hij bouwt met `SPATIE` (gewoon of hard, als stuk van een
 * `new RegExp`), en na een koppelteken laat hij de woordverbinder toe (`-\u2060?`).
 */
export const SPATIE = "[ \\u00a0]";

/**
 * Een aantal eenheden in een zin van de API ("som over 940 van de 1008 eenheden")
 * krijgt een duizendpunt, zoals "1.008" ernaast. Alleen een getal vlak voor
 * "eenheden" of "eenheid", zodat een jaartal ongemoeid blijft. De spatie kan gewoon of
 * hard zijn (ronde 17).
 */
export const metDuizendpunt = (tekst: string) =>
  tekst.replace(new RegExp(`\\b(\\d{4,})(?=${SPATIE}eenhe)`, "g"), (aantal) => getal(aantal));

/**
 * Getallen in een zin van de API in de schrijfwijze van het scherm: "1.55" →
 * "1,55" en "40%" → "40 %" (ook "50%–150%"). Een duizendtal met een punt
 * ("3.230") blijft staan: alleen een punt met één of twee cijfers erachter is
 * een decimaalteken.
 */
export const numberText = (text: string) =>
  text.replace(/(\d)\.(\d{1,2})(?!\d)/g, "$1,$2").replace(/(\d)%/g, "$1\u00a0%");

/**
 * Het zetwerk van een label of zin (ronde 7, voorheen `zet` in koers): de
 * eenheden van `keepUnits`, CO₂ met een onderschrift-2, en een losse letter
 * aan het eind ("E, F of G") die niet alleen op de volgende regel valt. De
 * woorden en getallen blijven die van de API.
 */
export const zetTekst = (text: string) =>
  keepUnits(text)
    .replace(/CO2/g, "CO₂")
    .replace(/ (\S)$/, "\u00a0$1");
